import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import BasicButton from "@components/button/BasicButton";
import UserImage from "@components/item/UserImage";
import { useTheme } from "@emotion/react";
import { Flex, Typography } from "antd";
import BackHeader from "@components/header/BackHeader";
import { queryClient } from "@libs/reactQuery/queryClient";
import { useTranslation } from "react-i18next";
const { Paragraph } = Typography;

const OrderDetail = () => {
  const { getOrderDetail, updateOrderConfirm } = useApiRequest();
  const params = useParams();
  const orderId = Number(params.orderId);
  const language = window.localStorage.getItem("language");
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  const { data: orderDetail } = useQuery({
    queryKey: ["orderDetail", orderId],
    queryFn: () => getOrderDetail(orderId),
    enabled: !!orderId,
  });

  const { mutate: confirmPurchase } = useMutation({
    mutationFn: (seller_id: number) =>
      updateOrderConfirm(orderId, seller_id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orderDetail", orderId] });
    },
  });

  return (
    <>
      <BackHeader title="주문상세" />

      <Container>
        <OrderHeader>
          <OrderDate>{orderDetail?.created_at}</OrderDate>
          <OrderStatus>
            {language === "ko"
              ? orderDetail?.order_status_name_ko
              : orderDetail?.order_status_name_en}
          </OrderStatus>
        </OrderHeader>
        {orderDetail?.seller_list?.map((seller: any) => (
          <SellerSection key={seller.seller_id}>
            <SellerInfo>
              <ProfileImage>
                <UserImage src={seller.seller_profile_image} size="4rem" />
              </ProfileImage>

              <SellerName>{seller.seller_nickname}</SellerName>
              <OrderStatus>
                {language === "ko"
                  ? seller.order_status_name_ko
                  : seller.order_status_name_en}
              </OrderStatus>
            </SellerInfo>

            {seller.product_list?.map((product: any) => (
              <ProductItem>
                <ProductInfo>
                  <ImageWrapper>
                    <ProductImage>
                      <Image
                        src={product.product_image}
                        width="6rem"
                        height="6rem"
                      />
                    </ProductImage>

                    {/* 상품 정보 */}
                    <ProductDetails>
                      <BrandName>
                        {language === "ko"
                          ? product.brand_name_ko
                          : product.brand_name_en || product.brand_name_ko}
                      </BrandName>
                      <ProductName>
                        {language === "ko"
                          ? product.product_name_ko
                          : product.product_name_en || product.product_name_ko}
                      </ProductName>

                      <OptionText>
                        {t("옵션")} : {product.color} / {product.size}
                      </OptionText>
                    </ProductDetails>
                  </ImageWrapper>

                  {/* 구독여부에 따른 금액 */}
                  {orderDetail?.is_discounted &&
                  product.product_condition === "NEW" ? (
                    <Price>
                      {t("[구독 멤버 할인 50%]")}{" "}
                      {(
                        product.product_price *
                        product.amount *
                        0.5
                      )?.toLocaleString()}{" "}
                      {t("원")} / {t("수량")}
                      {product.amount} {t("개")}
                    </Price>
                  ) : (
                    <Price>
                      {product.product_price?.toLocaleString()} {t("원")} /{" "}
                      {t("수량")} {product.amount} {t("개")}
                    </Price>
                  )}
                </ProductInfo>
              </ProductItem>
            ))}

            <PriceInfo>
              <PriceRow>
                <Label>{t("배송비")}</Label>
                <Value>
                  {seller.shipping_fee?.toLocaleString()} {t("원")}
                </Value>
              </PriceRow>

              <PriceRow>
                <Label>{t("상품 금액")}</Label>
                <Value>
                  {seller.total_sales_price?.toLocaleString()} {t("원")}
                </Value>
              </PriceRow>

              {orderDetail?.is_discounted && (
                <PriceRow>
                  <Label>{t("할인 금액")} </Label>
                  <DiscountValue>
                    -{" "}
                    {seller.total_payment_price === seller.total_sales_price
                      ? 0
                      : seller.total_payment_price?.toLocaleString()}{" "}
                    {t("원")}
                  </DiscountValue>
                </PriceRow>
              )}

              {/* 구매확정 버튼 */}
              {seller?.order_status === "2" && (
                <>
                  <ConfirmText>셀러가 발송완료한 상품입니다.</ConfirmText>
                  <BasicButton
                    title="구매 확정하기"
                    fontSize="1.6rem"
                    height="5.2rem"
                    width="100%"
                    mint
                    onClick={() => confirmPurchase(seller.seller_id)}
                  />
                </>
              )}

              {/* 리뷰 작성 버튼 */}
              {seller?.order_status === "4" && seller?.has_review === 0 && (
                <>
                  <BasicButton
                    title="리뷰 작성"
                    fontSize="1.6rem"
                    height="5.2rem"
                    width="100%"
                    point
                    onClick={() => {
                      navigate(`/add-review/${orderId}/${seller.seller_id}`);
                    }}
                  />
                </>
              )}
            </PriceInfo>
          </SellerSection>
        ))}
        {/* 총 금액 섹션 */}
        <TotalSection>
          <PriceRow>
            <Label>{t("총 배송비")}</Label>
            <Value>
              {orderDetail?.total_shipping_fee?.toLocaleString()} {t("원")}
            </Value>
          </PriceRow>

          <PriceRow>
            <Label>{t("총 상품 금액")}</Label>
            <Value>
              {orderDetail?.total_sales_price?.toLocaleString()} {t("원")}
            </Value>
          </PriceRow>

          {orderDetail?.is_discounted && (
            <PriceRow>
              <Label>{t("총할인 금액")}</Label>
              <Value>
                -{" "}
                {(
                  orderDetail?.total_sales_price -
                  orderDetail?.total_payment_price
                ).toLocaleString()}{" "}
                {t("원")}
              </Value>
            </PriceRow>
          )}
        </TotalSection>
        {/* 총 금액 */}
        <PaymentInfo>
          <Title>{t("결제방법")}</Title>
          <PriceRow>
            <Label>{t("결제 수단")}</Label>
            <Value>
              {language === "ko"
                ? orderDetail?.payment_method_ko
                : orderDetail?.payment_method_en}
            </Value>
          </PriceRow>

          {orderDetail?.payment_method_ko === "무통장입금" && (
            <PriceRow>
              <Label>{t("계좌번호")}</Label>

              <AccountBox>
                <div>
                  <Paragraph
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "0",
                    }}
                    copyable={{
                      text: "100-036-320333",
                      icon: [
                        <Image
                          src={theme.image.copy_icon}
                          width="1.6rem"
                          height="1.6rem"
                        />,
                      ],
                    }}>
                    (주)아울소싸이어티 신한은행 100-036-320333
                  </Paragraph>
                </div>
              </AccountBox>
            </PriceRow>
          )}

          <PriceRow>
            <Label>{t("결제 금액")}</Label>

            <Value>
              {(
                orderDetail?.total_payment_price +
                orderDetail?.total_shipping_fee
              ).toLocaleString()}{" "}
              {t("원")}
            </Value>
          </PriceRow>
        </PaymentInfo>

        {/* 배송지 정보 */}
        <DeliveryInfo>
          <Title>{t("배송지 정보")}</Title>

          <DeliveryRow>
            <Label>{t("받으시는 분")}</Label>
            <Value>{orderDetail?.receiver_name}</Value>
          </DeliveryRow>

          <DeliveryRow>
            <Label>{t("휴대폰 번호")}</Label>
            <Value>{orderDetail?.receiver_phone}</Value>
          </DeliveryRow>

          <DeliveryRow>
            <Label>{t("주소")}</Label>
            <Value>{`(${orderDetail?.shipping_postcode})
            ${orderDetail?.shipping_address} ${orderDetail?.shipping_address_detail}`}</Value>
          </DeliveryRow>
        </DeliveryInfo>
      </Container>
    </>
  );
};

export default OrderDetail;

const Container = styled.div(({ theme }) => ({
  backgroundColor: theme.color.white,
  padding: "2rem",
}));

const OrderHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: `1px solid ${theme.color.black}`,
  paddingBottom: "2rem",
}));

const OrderDate = styled.span(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
}));

const OrderStatus = styled.span(({ theme }) => ({
  fontSize: "1.4rem",
}));

const SellerSection = styled.div(({ theme }) => ({
  // padding: "2rem 0",
}));

const SellerInfo = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "2rem 2rem 0rem 2rem",
}));

const ProfileImage = styled.div({
  width: "4rem",
  height: "4rem",
  borderRadius: "50%",
  marginRight: "1rem",
  overflow: "hidden",
});

const SellerName = styled.span(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
  marginRight: "auto",
}));

const ProductItem = styled.div(({ theme }) => ({
  display: "flex",
  borderBottom: `1px solid ${theme.color.border_gray}`,
}));

const ProductImage = styled.div({
  width: "6rem",
  height: "6rem",
  objectFit: "cover",
});

const ProductInfo = styled.div({
  flex: 1,
});

const Price = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
  padding: "0rem 1.6rem 1.6rem 0rem",
}));

const PriceInfo = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "2rem 0rem 2rem 0",
  gap: "2rem",
  borderBottom: `1px solid ${theme.color.black}`,
}));

const PriceRow = styled.div({
  display: "flex",
  fontSize: "1.6rem",
  justifyContent: "space-between",
});

const Label = styled.span(({ theme }) => ({
  color: theme.color.gray,
  whiteSpace: "nowrap",
}));

const Value = styled.span(({ theme }) => ({}));

const DiscountValue = styled(Value)(({ theme }) => ({
  color: theme.color.gray,
}));

const TotalSection = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderBottom: "1px solid",
  gap: "2rem",
  borderTop: `4px solid ${theme.color.black}`,
  padding: "2rem 0",
}));

const PaymentInfo = styled.div(({ theme }) => ({
  padding: "2rem 0",
  borderBottom: `1px solid ${theme.color.gray}`,
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
}));

const DeliveryInfo = styled.div({
  padding: "2rem 0",
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
});

const Title = styled.div(({ theme }) => ({
  paddingBottom: "2rem",
  fontSize: "1.6rem",
  borderBottom: `1px solid ${theme.color.border_gray}`,
}));

const DeliveryRow = styled.div({
  fontSize: "1.6rem",
  display: "flex",
  justifyContent: "space-between",
});

const ImageWrapper = styled.div({
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  padding: "1.6rem 0 ",
  gap: "1rem",
});

const ProductDetails = styled.div({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: "1rem",
  flex: 1,
});

const BrandName = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
}));

const ProductName = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const OptionText = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const ConfirmText = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  marginBottom: "0.5rem",
}));

const AccountBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "1rem",
  fontSize: "1.4rem",

  "& > span": {
    fontSize: "1.2rem",
    lineHeight: "1.8rem",
  },

  "& > div": {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
}));
